import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { ActivatedRoute, ParamMap, Router } from '@angular/router';
import { Subject, of } from 'rxjs';
import {
  catchError,
  distinctUntilChanged,
  map,
  switchMap,
  takeUntil,
  tap,
} from 'rxjs/operators';
import { BlogService } from '../services/blog.service';
import { BlogDetail, BlogBlock } from './blog.models';
import { SeoService } from '../services/seo.service';
import { BreadcrumbsService } from '../services/breadcrumbs.service';

@Component({
  selector: 'app-blog-detail',
  templateUrl: './blog-detail.component.html',
  styleUrls: ['./blog-detail.component.scss'],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BlogDetailComponent implements OnInit, OnDestroy {
  detail?: BlogDetail;
  isLoading = true;
  errorMessage: string | null = null;

  private readonly destroy$ = new Subject<void>();
  private readonly jsonLdId = 'blog-article';

  constructor(
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly blogService: BlogService,
    private readonly seo: SeoService,
    private readonly breadcrumbs: BreadcrumbsService,
    private readonly cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.route.paramMap
      .pipe(
        map((params: ParamMap) => params.get('slug') || ''),
        distinctUntilChanged(),
        tap(() => this.resetState()),
        switchMap((slug) =>
          this.blogService.detail(slug).pipe(
            catchError(() => {
              this.handleError(
                'Dit artikel is niet gevonden of is niet langer beschikbaar.',
              );
              return of(null);
            }),
          ),
        ),
        takeUntil(this.destroy$),
      )
      .subscribe((detail) => {
        if (!detail) {
          return;
        }
        this.detail = detail;
        this.isLoading = false;
        this.errorMessage = null;
        this.applySeo(detail);
        this.breadcrumbs.setCurrentLeafLabel(detail.title);
        this.cdr.markForCheck();
      });
  }

  /**
   * Combines author name and published date for compact display.
   * Examples:
   *  - "Jane Doe · 12 oktober 2025"
   *  - "12 oktober 2025" (no author)
   *  - "Jane Doe" (no date)
   */
  get authorAndDate(): string | null {
    if (!this.detail) { return null; }
    const author = this.detail.authorName?.trim();
    // Show the immutable createdAt, not publishedAt (which Strapi re-stamps on
    // every republish). publishedAt is only a defensive fallback. (BLA-7 Q1)
    const displayDate = this.detail.createdAt || this.detail.publishedAt;
    const date = displayDate ? this.formatPublishedDate(displayDate) : undefined;
    if (author && date) { return `${author} · ${date}`; }
    return author || date || null;
  }

  /** The displayed publication date on its own (immutable createdAt). Used in the
   *  template when the author is a clickable link, so author and date render
   *  separately. (BLA-5 / BLA-7 Q1) */
  get displayDate(): string | null {
    if (!this.detail) { return null; }
    const d = this.detail.createdAt || this.detail.publishedAt;
    return d ? this.formatPublishedDate(d) : null;
  }

  /**
   * "Laatste wijziging" date, shown only when the post was genuinely edited on a
   * later calendar day than it was first created (BLA-7 Q1). Same-day edits and
   * clock-skew (updatedAt < createdAt) are suppressed as noise. The day is keyed
   * in Europe/Amsterdam so the line is stable regardless of where it renders
   * (build server in UTC vs. browser in local time).
   */
  get lastModified(): string | null {
    if (!this.detail) { return null; }
    const created = this.detail.createdAt;
    const updated = this.detail.updatedAt;
    if (!created || !updated) { return null; }
    const createdMs = Date.parse(created);
    const updatedMs = Date.parse(updated);
    if (Number.isNaN(createdMs) || Number.isNaN(updatedMs)) { return null; }
    if (updatedMs <= createdMs) { return null; }
    if (this.amsterdamDayKey(created) === this.amsterdamDayKey(updated)) { return null; }
    return this.formatPublishedDate(updated);
  }

  private amsterdamDayKey(iso: string): string {
    try {
      return new Intl.DateTimeFormat('en-CA', {
        timeZone: 'Europe/Amsterdam',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
      }).format(new Date(iso));
    } catch {
      return iso.slice(0, 10);
    }
  }

  private formatPublishedDate(iso: string): string {
    try {
      const d = new Date(iso);
      // Use Dutch locale as other date formatting already does (nl-NL)
      return d.toLocaleDateString('nl-NL', { day: 'numeric', month: 'long', year: 'numeric' });
    } catch {
      return iso;
    }
  }

  ngOnDestroy(): void {
    this.seo.removeJsonLd(this.jsonLdId);
    this.destroy$.next();
    this.destroy$.complete();
  }

  trackBlock(_index: number, block: BlogBlock): string {
    return `${block.type}-${_index}`;
  }

  goBack(): void {
    this.router.navigate(['/stories']);
  }

  private resetState(): void {
    this.detail = undefined;
    this.isLoading = true;
    this.errorMessage = null;
    this.seo.removeJsonLd(this.jsonLdId);
    this.cdr.markForCheck();
  }

  private handleError(message: string): void {
    this.isLoading = false;
    this.errorMessage = message;
    this.seo.updateSeo({
      title: 'Artikel niet gevonden',
      description: message,
      robots: 'noindex,follow',
    });
    this.cdr.markForCheck();
  }

  private applySeo(detail: BlogDetail): void {
    const title = detail.seo?.title || detail.title;
    const description =
      detail.seo?.description || detail.excerpt || 'Blogartikel van Blauwtand.';
    const image = detail.seo?.ogImageUrl || detail.coverImageUrl || undefined;
    const canonical = this.seo.generateCanonical(`/stories/${detail.slug}`);

    this.seo.updateSeo({
      title,
      description,
      canonicalUrl: canonical,
      ogType: 'article',
      imageUrl: image,
    });

    const articleJsonLd: Record<string, unknown> = {
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline: title,
      description,
      // `datePublished` must be the immutable original date. Strapi re-stamps
      // `publishedAt` on every republish, so `createdAt` (which never changes)
      // backs it; `publishedAt` remains only as a defensive fallback.
      datePublished: detail.createdAt ?? detail.publishedAt ?? undefined,
      dateModified:
        detail.updatedAt ?? detail.createdAt ?? detail.publishedAt ?? undefined,
      image: image ? [image] : undefined,
      author: this.buildAuthorJsonLd(detail),
      publisher: {
        '@type': 'Organization',
        name: 'Blauwtand',
        url: 'https://blauwtand.com/',
      },
      mainEntityOfPage: {
        '@type': 'WebPage',
        '@id': canonical,
      },
    };

    this.seo.addJsonLd(this.jsonLdId, articleJsonLd);
  }

  /**
   * schema.org author node. Three branches:
   *  - linked team member -> Person with a url to the /auteur page (BLA-5);
   *  - guest authorName only -> Person, name only;
   *  - neither -> the Blauwtand Organization.
   */
  private buildAuthorJsonLd(detail: BlogDetail): Record<string, unknown> {
    if (detail.author) {
      return {
        '@type': 'Person',
        name: detail.author.name,
        url: this.seo.generateCanonical(`/auteur/${detail.author.slug}`),
      };
    }
    if (detail.authorName) {
      return { '@type': 'Person', name: detail.authorName };
    }
    return { '@type': 'Organization', name: 'Blauwtand' };
  }
}
